import { FC } from 'react'
import CheckBox from './CheckBox'
import Label from './label'
import Hint from '@components/Hint'
import DateInput, { ChangeEventParams } from './DateInput'
import { YEAR_ONLY_CUTOFF_YEAR } from '../../constants/date'
import Notice from '@components/Notice'

const hintText = 'Check this box if your date is BCE/BC/a. C. (before the Common Era/before Christ/antes de Cristo).'

export type DateEntryValue = {
  isBCE: boolean
} & ChangeEventParams['value']

export type OnChangeEvent = {
  name: string
  value: DateEntryValue
}

export type DateEntryProps = {
  name: string
  value: DateEntryValue
  onChange: (params: OnChangeEvent) => void
  legend?: string
}

type CheckboxChangeEvent = {
  checked: boolean
}

export const noticeMessage = `Dates before ${YEAR_ONLY_CUTOFF_YEAR} CE will only record the year.`

const DateEntry: FC<DateEntryProps> = ({ name, value, onChange, legend = 'Select date' }) => {
  const { isBCE, ...dateValue } = value

  const Timetoast = window.Timetoast || {}
  const dateFormat = Timetoast.preferredDateInputFormat || 'MM-DD-YYYY'

  const onCheckboxChange = (change: CheckboxChangeEvent) => {
    const { checked } = change
    const changeEvent = {
      name,
      value: {
        ...value,
        isBCE: checked,
      },
    }
    onChange(changeEvent)
  }

  const onInputChange = ({ value: newInputValue }: ChangeEventParams) => {
    onChange({
      name,
      value: {
        isBCE: value.isBCE,
        ...newInputValue,
      },
    })
  }

  const onCalendarChange = ({ value: newInputValue }: ChangeEventParams) => {
    onChange({
      name,
      value: {
        isBCE: false,
        ...newInputValue,
      },
    })
  }

  const yearAsNumber = Number(value.year ?? '')
  const isBelowYearOnlyCutOff = isBCE || (yearAsNumber < YEAR_ONLY_CUTOFF_YEAR && yearAsNumber !== 0)

  return (
    <fieldset className="entry-date-picker">
      <legend className="legend">
        {legend} <span className="label-tip">(Month/Day optional)</span>
      </legend>
      <DateInput
        isBce={isBCE}
        id={name}
        name={name}
        value={dateValue}
        onInputChange={onInputChange}
        onCalendarChange={onCalendarChange}
        dateFormat={dateFormat}
        isBelowYearOnlyCutOff={isBelowYearOnlyCutOff}
      />

      <div className="entry-date-picker-bce-check">
        <CheckBox id={`${name}-bce-checkbox`} checked={isBCE} onChange={onCheckboxChange} />
        <Label htmlFor={`${name}-bce-checkbox`}>Is Before Common Era (BCE/BC)</Label>
        <Hint hint={hintText} />
      </div>
      {isBelowYearOnlyCutOff && (
        <Notice layout="horizontal" className="block--flush" body={noticeMessage} size="mini" />
      )}
    </fieldset>
  )
}

export default DateEntry
